import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  AlertTriangle, ArrowUpRight, MessageSquare, PhoneCall,
  Calendar as CalendarIcon, Phone, Zap, TrendingUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function UsageDashboardWidget({ onUpgrade }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);

  const fetchUsage = useCallback(async () => {
    try {
      const { data } = await (await import('axios')).default.get(`${API}/billing/usage-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsage(data);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  if (!usage || !usage.has_subscription) return null;

  const { sms, voice, calls_handled, showings_booked, warning_level, next_reset, tier } = usage;

  return (
    <div className="space-y-3" data-testid="usage-dashboard-widget">
      {/* Warning Banner */}
      {warning_level !== 'ok' && (
        <UsageWarningBanner level={warning_level} smsPct={sms.pct} voicePct={voice.pct} onUpgrade={onUpgrade} />
      )}

      {/* Compact Usage Card */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-amber-400" />
              <h3 className="text-sm font-medium text-foreground">Plan Usage</h3>
              <Badge className="text-[9px] bg-muted text-muted-foreground border-border capitalize">{tier || 'free'}</Badge>
            </div>
            <Button size="sm" variant="ghost" className="text-[10px] text-muted-foreground hover:text-foreground gap-1 h-6 px-2"
              onClick={() => navigate('/settings/billing')} data-testid="goto-billing-from-usage">
              Manage <ArrowUpRight size={10} />
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* SMS Usage */}
            <div data-testid="dash-sms-usage">
              <div className="flex items-center gap-1.5 mb-1">
                <MessageSquare size={11} className="text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">SMS</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${usagePctColor(sms.pct)}`}
                  style={{ width: `${Math.min(100, sms.pct)}%` }} />
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">{sms.used}/{sms.limit}</p>
            </div>

            {/* Voice Usage */}
            <div data-testid="dash-voice-usage">
              <div className="flex items-center gap-1.5 mb-1">
                <PhoneCall size={11} className="text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">AI Minutes</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${usagePctColor(voice.pct)}`}
                  style={{ width: `${Math.min(100, voice.pct)}%` }} />
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">{voice.used}/{voice.limit} min</p>
            </div>

            {/* Calls Handled */}
            <div data-testid="dash-calls-handled">
              <div className="flex items-center gap-1.5 mb-1">
                <Phone size={11} className="text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Calls</span>
              </div>
              <p className="text-lg font-bold text-foreground">{calls_handled}</p>
              <p className="text-[10px] text-muted-foreground">this period</p>
            </div>

            {/* Showings Booked */}
            <div data-testid="dash-showings-booked">
              <div className="flex items-center gap-1.5 mb-1">
                <CalendarIcon size={11} className="text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Showings</span>
              </div>
              <p className="text-lg font-bold text-foreground">{showings_booked}</p>
              <p className="text-[10px] text-muted-foreground">this period</p>
            </div>
          </div>

          {/* Next reset */}
          {next_reset && (
            <p className="text-[10px] text-muted-foreground mt-2">
              Resets {new Date(next_reset).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UsageWarningBanner({ level, smsPct, voicePct, onUpgrade }) {
  const config = {
    warning: {
      bg: 'bg-amber-500/5 border-amber-500/20',
      icon: 'text-amber-400',
      title: 'Approaching plan limits',
      desc: `You've used ${Math.max(smsPct, voicePct)}% of your plan's resources.`,
    },
    critical: {
      bg: 'bg-orange-500/5 border-orange-500/20',
      icon: 'text-orange-400',
      title: 'Almost at plan limits',
      desc: `You've used ${Math.max(smsPct, voicePct)}% of your plan's resources. Upgrade to avoid overages.`,
    },
    exceeded: {
      bg: 'bg-red-500/5 border-red-500/20',
      icon: 'text-red-400',
      title: 'Plan limits exceeded',
      desc: 'Your AI keeps working but overage charges will apply. Upgrade for higher limits.',
    },
  };

  const c = config[level] || config.warning;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${c.bg}`} data-testid={`usage-warning-${level}`}>
      <AlertTriangle size={16} className={`${c.icon} shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${c.icon}`}>{c.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{c.desc}</p>
      </div>
      {onUpgrade && (
        <Button size="sm" onClick={() => onUpgrade('limit_hit')}
          className="bg-amber-500 hover:bg-amber-400 text-black text-xs shrink-0 gap-1"
          data-testid="upgrade-from-warning">
          <TrendingUp size={12} /> Upgrade
        </Button>
      )}
    </div>
  );
}

export function FeatureGate({ feature, tier, children, fallback }) {
  const featureRequirements = {
    premium_voices: 'pro',
    multilingual: 'pro',
    per_language_voices: 'pro',
    advanced_automation: 'elite',
    priority_support: 'elite',
  };

  const tierOrder = { free: 0, starter: 1, pro: 2, elite: 3 };
  const requiredTier = featureRequirements[feature] || 'pro';
  const currentLevel = tierOrder[tier] || 0;
  const requiredLevel = tierOrder[requiredTier] || 2;

  if (currentLevel >= requiredLevel) {
    return children;
  }

  return fallback || (
    <div className="relative opacity-50 pointer-events-none select-none" data-testid={`gated-${feature}`}>
      {children}
      <div className="absolute inset-0 flex items-center justify-center">
        <Badge className="bg-card/90 text-amber-400 border-amber-500/20 text-xs gap-1">
          <Zap size={10} /> Requires {requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1)}
        </Badge>
      </div>
    </div>
  );
}

function usagePctColor(pct) {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 90) return 'bg-orange-500';
  if (pct >= 70) return 'bg-amber-500';
  return 'bg-emerald-500';
}
